/**
 * PSP/CRE Tool v2.1 - Mental Health Constants
 * 
 * Defines Combat Stress Reaction (CSR), PTSD rates, behavioral health
 * casualty estimation, rotation schedules, and mental health support requirements.
 * 
 * @fileoverview Mental health planning factors and CSR rates
 * @module constants/mentalHealth
 * @author Dr. Tatar Radu MD - Romanian Military Medical Institute
 * @date December 22, 2024
 * @version 2.1 - icons.js Integration + Ukraine 2022-2024 data
 * 
 * Key Concepts:
 * - CSR (Combat Stress Reaction): Immediate response to combat stress
 * - COSR (Combat Operational Stress Reaction): NATO terminology
 * - PTSD: Post-Traumatic Stress Disorder (diagnosed condition)
 * - BICEPS: Brevity, Immediacy, Centrality, Expectancy, Proximity, Simplicity
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (STATUS_ICONS, MEDICAL_ICONS)
 * - Added getCSRIntensityOptions(), getRotationOptions(), getRiskFactorOptions()
 * - Added validateMentalHealthConfig() with enhanced validation
 * - Enhanced Ukraine 2022-2024 psychological data
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  MEDICAL_ICONS,
  TIME_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  
  // Medical/Mental health icons
  BRAIN: MEDICAL_ICONS?.BRAIN || '🧠',
  HEART: MEDICAL_ICONS?.HEART || '❤️',
  
  // Time icons
  CLOCK: TIME_ICONS?.CLOCK || '⏰',
  
  // Additional
  WARNING: '⚠️',
  PERSON: '👤'
};

// ============================================================================
// COMBAT STRESS REACTION (CSR) RATES
// ============================================================================

/**
 * Combat stress volume model (2026-10-10, Dr. Tatar, option A of docs/research/CSR_VOLUME_CHECK.md):
 *   cases per day = baseline psychiatric rate x strength  +  wia_ratio x wounded of the day
 * The baseline is the psychiatric DNBI casualty rate of a US brigade in Iraq (Belmont et al., Military Medicine 2010:
 * 59.8 per 1,000 soldier combat-years = 0.16 per 1,000 per day). wia_ratio by intensity (below) follows the planning
 * ratios of stress to wounded (NATO AJMedP-1: 11% of WIA; high-intensity war 1 to 3; up to 1 to 1 in intense fighting).
 * The old daily_csr_rate (per 1,000 of the whole strength, 0.5 to 30) is kept in each block for reference; it is no
 * longer used by the casualty calculator (personnelIntelligence's CSR projection still reads it).
 * LOW_INTENSITY return to duty 0.85 is an interpolation; MODERATE 0.80 is the NATO figure (80% return to duty).
 */
/**
 * Unit cohesion factor on combat stress volume (moved here from dnbiModule, 2026-10-11) and the mapping from Tab 1's
 * morale levels to it. Before, the calculator passed the morale key (FANATICAL, HIGH, NORMAL, LOW, BROKEN, ROUTED) to a
 * table keyed EXCELLENT, GOOD, ADEQUATE, POOR, BROKEN: only BROKEN matched, every other morale silently counted as 1.0.
 * The factors 0.7 to 2.0 are PLACEHOLDERS carried over from the old literal (no source). ROUTED uses the BROKEN factor.
 */
export const CSR_COHESION_FACTORS = {
  EXCELLENT: 0.7,
  GOOD: 0.85,
  ADEQUATE: 1.0,
  POOR: 1.3,
  BROKEN: 2.0
};
export const CSR_COHESION_STATUS = { status: 'PLACEHOLDER', basis: 'Carried over from the old code literal; no source.' };
export const MORALE_TO_COHESION = {
  FANATICAL: 'EXCELLENT',
  HIGH: 'GOOD',
  NORMAL: 'ADEQUATE',
  LOW: 'POOR',
  BROKEN: 'BROKEN',
  ROUTED: 'BROKEN'
};

export const CSR_WIA_MODEL = {
  baseline_per_1000_per_day: 0.16,
  // Growth of combat stress with days in action (used in dnbiModule.calculateCSRCasualties). Both are PLACEHOLDERS with
  // no source: literature gives a risk that rises with intensity and exposure (WWII judgment: effectiveness declines
  // after about 90 days, breakdown between 60 and 240 days) but no day-by-day curve. Because the volume is now anchored
  // to the wounded, part of this growth may already be inside the WIA term (to review).
  duration_growth_per_day: { value: 0.03, status: 'PLACEHOLDER', basis: 'Replaced an older 0.05 linear rule; unsourced.' },
  duration_growth_after_sustainable_per_day: { value: 0.15, status: 'PLACEHOLDER', basis: 'Unsourced; applies after sustainable_days of each intensity.' },
  status: 'PLACEHOLDER',
  source: 'Belmont 2010 psychiatric DNBI rate (baseline); NATO AJMedP-1 and planning ratios (wia_ratio).'
};

export const CSR_RATES = {
  BY_COMBAT_INTENSITY: {
    PERMISSIVE: {
      code: 'PERMISSIVE',
      label: 'Permissive Environment',
      description: 'Peacekeeping, stability operations, minimal combat',
      icon: ICONS.GREEN,
      color: '#4CAF50',
      
      daily_csr_rate: 0.0005,
      // 2026-10-10 (docs/research/CSR_VOLUME_CHECK.md): combat stress cases per wounded man. No combat: no battle stress; only the baseline psychiatric rate applies.
      wia_ratio: 0.0,
      cumulative_30_day: 0.015,
      cumulative_90_day: 0.04,
      
      presentation: {
        acute_stress: 0.60,
        adjustment_disorder: 0.30,
        anxiety_disorder: 0.05,
        other: 0.05
      },
      
      rtd_rate_24h: 0.70,
      rtd_rate_72h: 0.90,
      evacuation_rate: 0.02,
      sustainable_days: 365,
      
      educational: {
        key_point: 'Low combat stress - most cases RTD quickly',
        teaching_note: 'Even permissive environments have cumulative stress'
      }
    },
    
    LOW_INTENSITY: {
      code: 'LOW_INTENSITY',
      label: 'Low Intensity',
      description: 'Counterinsurgency, occasional contact, indirect fire threat',
      icon: ICONS.YELLOW,
      color: '#8BC34A',
      
      daily_csr_rate: 0.002,
      // 2026-10-10 (docs/research/CSR_VOLUME_CHECK.md): combat stress cases per wounded man. 1 stress case to 10 wounded in low-level conflict (planning ratios quoted for US Army and USMC manuals).
      wia_ratio: 0.1,
      cumulative_30_day: 0.06,
      cumulative_90_day: 0.15,
      
      presentation: {
        acute_stress: 0.50,
        adjustment_disorder: 0.35,
        ptsd_symptoms: 0.10,
        other: 0.05
      },
      
      rtd_rate_24h: 0.55,
      rtd_rate_72h: 0.85,
      evacuation_rate: 0.05,
      sustainable_days: 180,
      
      educational: {
        key_point: 'IED threat creates persistent hypervigilance',
        teaching_note: 'Moderate stress accumulation over time'
      }
    },
    
    MODERATE_INTENSITY: {
      code: 'MODERATE_INTENSITY',
      label: 'Moderate Intensity',
      description: 'Regular combat contact, conventional operations, sustained threat',
      icon: ICONS.ORANGE,
      color: '#FFC107',
      
      daily_csr_rate: 0.005,
      // 2026-10-10 (docs/research/CSR_VOLUME_CHECK.md): combat stress cases per wounded man. NATO AJMedP-1 planning: battle stress cases are an additional 11% of WIA.
      wia_ratio: 0.11,
      cumulative_30_day: 0.15,
      cumulative_90_day: 0.35,
      
      presentation: {
        acute_stress: 0.45,
        adjustment_disorder: 0.30,
        ptsd_symptoms: 0.15,
        dissociative: 0.05,
        other: 0.05
      },
      
      rtd_rate_24h: 0.40,
      rtd_rate_72h: 0.80,
      evacuation_rate: 0.10,
      sustainable_days: 90,
      
      educational: {
        key_point: 'Significant stress - forward treatment essential',
        teaching_note: 'This is typical conventional warfare intensity'
      }
    },
    
    HIGH_INTENSITY: {
      code: 'HIGH_INTENSITY',
      label: 'High Intensity',
      description: 'Sustained heavy combat, peer/near-peer conflict, artillery threat',
      icon: ICONS.RED,
      color: '#FF9800',
      
      daily_csr_rate: 0.015,
      // 2026-10-10 (docs/research/CSR_VOLUME_CHECK.md): combat stress cases per wounded man. At least 1 stress case to 3 wounded in high-intensity war (planning ratio quoted for a US Army manual).
      wia_ratio: 0.33,
      cumulative_30_day: 0.35,
      cumulative_90_day: 0.65,
      
      presentation: {
        acute_stress: 0.40,
        adjustment_disorder: 0.25,
        ptsd_symptoms: 0.20,
        dissociative: 0.08,
        conversion: 0.05,
        other: 0.02
      },
      
      rtd_rate_24h: 0.25,
      rtd_rate_72h: 0.50,
      evacuation_rate: 0.20,
      sustainable_days: 30,
      
      ukraine_observations: {
        primary_stressor: 'ARTILLERY_BOMBARDMENT',
        secondary_stressor: 'DRONE_SURVEILLANCE',
        tertiary_stressor: 'TRENCH_CONDITIONS',
        artillery_effect: 'Constant threat of instant death causes severe anxiety',
        drone_effect: 'Feeling of being constantly watched creates hypervigilance',
        trench_effect: 'Confined conditions, mud, cold compound stress'
      },
      
      educational: {
        key_point: 'Ukraine 2022-2024: 30-40% CSR in frontline units',
        teaching_note: 'High attrition from mental health'
      }
    },
    
    EXTREME_INTENSITY: {
      code: 'EXTREME_INTENSITY',
      label: 'Extreme Intensity',
      description: 'WWII/Bakhmut-level combat, encirclement, mass casualties daily',
      icon: ICONS.RED,
      color: '#F44336',
      
      daily_csr_rate: 0.030,
      // 2026-10-10 (docs/research/CSR_VOLUME_CHECK.md): combat stress cases per wounded man. Up to 1 to 1 in the most intense fighting (planning ratios; Israel 1973 about 23% of non-fatal casualties).
      wia_ratio: 1.0,
      cumulative_30_day: 0.60,
      cumulative_90_day: 0.85,
      
      presentation: {
        acute_stress: 0.35,
        adjustment_disorder: 0.20,
        ptsd_symptoms: 0.25,
        dissociative: 0.10,
        conversion: 0.05,
        psychotic_features: 0.03,
        other: 0.02
      },
      
      rtd_rate_24h: 0.15,
      rtd_rate_72h: 0.35,
      evacuation_rate: 0.35,
      sustainable_days: 14,
      
      educational: {
        key_point: 'Psychiatric casualties may equal physical casualties',
        teaching_note: 'Bakhmut 2023 comparable to WWII rates'
      }
    }
  },
  
  RISK_FACTORS: {
    FIRST_COMBAT: {
      code: 'FIRST_COMBAT',
      label: 'First Combat Exposure',
      icon: ICONS.WARNING,
      multiplier: 1.5,
      description: 'Soldiers in first combat have 50% higher CSR rate',
      duration_weeks: 2,
      mitigation: 'Pair with experienced soldiers, pre-deployment stress inoculation'
    },
    
    PROLONGED_DEPLOYMENT: {
      code: 'PROLONGED_DEPLOYMENT',
      label: 'Prolonged Deployment (>6 months)',
      icon: ICONS.WARNING,
      multiplier: 1.3,
      description: 'Cumulative stress increases CSR risk',
      threshold_days: 180,
      progressive: true,
      mitigation: 'R&R leave, mid-tour rotation if possible'
    },
    
    MULTIPLE_DEPLOYMENTS: {
      code: 'MULTIPLE_DEPLOYMENTS',
      label: 'Multiple Deployments',
      icon: ICONS.WARNING,
      multiplier_per_deployment: 1.15,
      max_multiplier: 2.0,
      description: 'Each deployment increases baseline risk',
      mitigation: 'Screening between deployments, reset periods'
    },
    
    UNIT_CASUALTIES_HIGH: {
      code: 'UNIT_CASUALTIES_HIGH',
      label: 'High Unit Casualties (>10%)',
      icon: ICONS.RED,
      multiplier: 1.8,
      threshold_percent: 10,
      description: 'Losing buddies significantly increases CSR',
      mitigation: 'After-action debriefs, memorial rituals'
    },
    
    UNIT_CASUALTIES_SEVERE: {
      code: 'UNIT_CASUALTIES_SEVERE',
      label: 'Severe Unit Casualties (>30%)',
      icon: ICONS.RED,
      multiplier: 2.5,
      threshold_percent: 30,
      description: 'Unit cohesion breakdown threshold',
      mitigation: 'Consider unit reconstitution vs individual replacement'
    },
    
    LEADERSHIP_LOSS: {
      code: 'LEADERSHIP_LOSS',
      label: 'Leader Killed/Wounded',
      icon: ICONS.RED,
      multiplier: 1.4,
      duration_days: 14,
      description: 'Loss of trusted leader destabilizes unit',
      mitigation: 'Rapid leadership succession, commander visibility'
    },
    
    SLEEP_DEPRIVATION: {
      code: 'SLEEP_DEPRIVATION',
      label: 'Sleep Deprivation (<4h/day)',
      icon: ICONS.ORANGE,
      multiplier: 1.6,
      threshold_hours: 4,
      description: 'Sleep loss dramatically increases stress vulnerability',
      mitigation: 'Enforce sleep rotation, protect rest periods'
    }
  },
  
  PROTECTIVE_FACTORS: {
    UNIT_COHESION: {
      code: 'UNIT_COHESION',
      label: 'Strong Unit Cohesion',
      icon: ICONS.GREEN,
      modifier: 0.7,
      description: 'Strongest protective factor against CSR',
      how_to_maintain: 'Keep teams together, buddy system, shared experiences'
    },
    
    EXPERIENCED_LEADERSHIP: {
      code: 'EXPERIENCED_LEADERSHIP',
      label: 'Experienced Leadership',
      icon: ICONS.GREEN,
      modifier: 0.8,
      description: 'Experienced leaders reduce unit stress',
      how_to_maintain: 'Leader continuity, trust-building'
    },
    
    ADEQUATE_REST: {
      code: 'ADEQUATE_REST',
      label: 'Adequate Rest (>6h sleep)',
      icon: ICONS.GREEN,
      modifier: 0.75,
      description: 'Rest is critical for stress recovery',
      how_to_maintain: 'Enforce sleep schedules, protect rest time'
    },
    
    REGULAR_ROTATION: {
      code: 'REGULAR_ROTATION',
      label: 'Regular Rotation from Combat',
      icon: ICONS.GREEN,
      modifier: 0.6,
      description: 'Time away from combat essential',
      how_to_maintain: '3-4 day rotation cycles from frontline'
    }
  }
};

// ============================================================================
// ROTATION SCHEDULES
// ============================================================================

export const ROTATION_SCHEDULES = {
  HIGH_INTENSITY: {
    code: 'HIGH_INTENSITY',
    label: 'High Intensity Combat',
    icon: ICONS.RED,
    
    frontline_days: 3,
    rest_days: 2,
    max_continuous_days: 5,
    
    cycle: {
      frontline: 3,
      reserve: 2,
      rear: 1,
      total_cycle_days: 6
    },
    
    rationale: 'Ukraine data shows 3-4 day rotation critical for sustainment',
    educational: {
      key_point: 'Max 3 days frontline without rotation',
      ukraine_lesson: '3-4 day rotation cycles dramatically reduce CSR'
    }
  },
  
  MODERATE_INTENSITY: {
    code: 'MODERATE_INTENSITY',
    label: 'Moderate Intensity Combat',
    icon: ICONS.ORANGE,
    
    frontline_days: 7,
    rest_days: 3,
    max_continuous_days: 10,
    
    cycle: {
      frontline: 7,
      reserve: 3,
      rear: 2,
      total_cycle_days: 12
    },
    
    rationale: 'Standard conventional warfare rotation',
    educational: {
      key_point: '7 days frontline max without rest'
    }
  },
  
  LOW_INTENSITY: {
    code: 'LOW_INTENSITY',
    label: 'Low Intensity Operations',
    icon: ICONS.YELLOW,
    
    frontline_days: 14,
    rest_days: 4,
    max_continuous_days: 21,
    
    cycle: {
      frontline: 14,
      reserve: 4,
      rear: 3,
      total_cycle_days: 21
    },
    
    rationale: 'Peacekeeping/COIN standard rotation',
    educational: {
      key_point: '14-day patrol cycles typical'
    }
  },
  
  PERMISSIVE: {
    code: 'PERMISSIVE',
    label: 'Permissive Environment',
    icon: ICONS.GREEN,
    
    frontline_days: 30,
    rest_days: 7,
    max_continuous_days: 45,
    
    cycle: {
      frontline: 30,
      reserve: 7,
      rear: 7,
      total_cycle_days: 44
    },
    
    rationale: 'Training/peacetime standard',
    educational: {
      key_point: 'Standard garrison training cycle'
    }
  }
};

// ============================================================================
// BICEPS PRINCIPLES
// ============================================================================

export const BICEPS_PRINCIPLES = {
  name: 'BICEPS',
  full_name: 'Brevity, Immediacy, Centrality, Expectancy, Proximity, Simplicity',
  icon: ICONS.BRAIN,
  
  principles: {
    BREVITY: {
      code: 'B',
      name: 'Brevity',
      icon: ICONS.CLOCK,
      description: 'Treatment is short - 1-3 days',
      implementation: 'Focus on immediate recovery, not long-term therapy',
      rationale: 'Prolonged treatment reinforces sick role'
    },
    IMMEDIACY: {
      code: 'I',
      name: 'Immediacy',
      icon: '⚡',
      description: 'Treatment begins as soon as possible',
      implementation: 'Address symptoms within hours, not days',
      rationale: 'Early intervention prevents chronic symptoms'
    },
    CENTRALITY: {
      code: 'C',
      name: 'Centrality',
      icon: '🎯',
      description: 'Treatment in central location near unit',
      implementation: 'Not in hospital - in restoration area',
      rationale: 'Hospital environment reinforces sick role'
    },
    EXPECTANCY: {
      code: 'E',
      name: 'Expectancy',
      icon: '✅',
      description: 'Clear expectation of return to duty',
      implementation: 'Communicate RTD as normal outcome',
      rationale: 'Expectation of recovery drives recovery'
    },
    PROXIMITY: {
      code: 'P',
      name: 'Proximity',
      icon: '📍',
      description: 'Treatment near soldier\'s unit',
      implementation: 'As close to unit as tactically feasible',
      rationale: 'Maintains connection to unit identity'
    },
    SIMPLICITY: {
      code: 'S',
      name: 'Simplicity',
      icon: '📋',
      description: 'Simple treatment - rest, food, reassurance',
      implementation: 'Basic needs, not complex therapy',
      rationale: 'Complex treatment implies complex problem'
    }
  },
  
  educational: {
    key_point: 'BICEPS maximizes RTD from combat stress',
    teaching_note: '90%+ RTD when BICEPS applied correctly'
  }
};

// ============================================================================
// PTSD RATES
// ============================================================================

export const PTSD_RATES = {
  BY_DEPLOYMENT_TYPE: {
    PEACEKEEPING: {
      code: 'PEACEKEEPING',
      label: 'Peacekeeping Operations',
      icon: ICONS.GREEN,
      ptsd_prevalence_percent: 5,
      depression_percent: 8,
      anxiety_percent: 10
    },
    CONVENTIONAL_LOW: {
      code: 'CONVENTIONAL_LOW',
      label: 'Conventional - Low Intensity',
      icon: ICONS.YELLOW,
      ptsd_prevalence_percent: 10,
      depression_percent: 12,
      anxiety_percent: 15
    },
    CONVENTIONAL_MODERATE: {
      code: 'CONVENTIONAL_MODERATE',
      label: 'Conventional - Moderate',
      icon: ICONS.ORANGE,
      ptsd_prevalence_percent: 18,
      depression_percent: 18,
      anxiety_percent: 22
    },
    CONVENTIONAL_HIGH: {
      code: 'CONVENTIONAL_HIGH',
      label: 'Conventional - High Intensity',
      icon: ICONS.RED,
      ptsd_prevalence_percent: 30,
      depression_percent: 25,
      anxiety_percent: 30
    },
    EXTREME_COMBAT: {
      code: 'EXTREME_COMBAT',
      label: 'Extreme Combat (Bakhmut-level)',
      icon: ICONS.RED,
      ptsd_prevalence_percent: 45,
      depression_percent: 35,
      anxiety_percent: 40
    }
  },
  
  RISK_FACTORS: {
    PRIOR_TRAUMA: {
      label: 'Prior Trauma History',
      odds_ratio: 2.5,
      icon: ICONS.WARNING
    },
    PRIOR_MENTAL_HEALTH: {
      label: 'Prior Mental Health Issues',
      odds_ratio: 2.8,
      icon: ICONS.WARNING
    },
    LOW_SOCIAL_SUPPORT: {
      label: 'Low Social Support',
      odds_ratio: 1.8,
      icon: ICONS.WARNING
    },
    WOUNDED_IN_COMBAT: {
      label: 'Wounded in Combat',
      odds_ratio: 3.2,
      icon: ICONS.RED
    },
    WITNESSED_ATROCITIES: {
      label: 'Witnessed Atrocities',
      odds_ratio: 2.9,
      icon: ICONS.RED
    },
    KILLED_IN_COMBAT: {
      label: 'Killed Enemy in Combat',
      odds_ratio: 1.6,
      icon: ICONS.ORANGE
    }
  }
};

// ============================================================================
// MENTAL HEALTH SUPPORT REQUIREMENTS
// ============================================================================

export const MENTAL_HEALTH_SUPPORT = {
  // CALC-014 (2026-10-09): moved here from a literal in personnelIntelligence (value unchanged; it is a
  // coarser planning figure than the STAFFING ratios below and should be reconciled with them).
  PLANNING: { mh_personnel_per_5000_troops: 2 },

  STAFFING: {
    BATTALION: {
      COMBAT_MEDIC_MH: {
        role: 'Combat Medic (MH trained)',
        ratio: '1:200',
        duties: ['First responder', 'Basic assessment', 'BICEPS application']
      }
    },
    BRIGADE: {
      BEHAVIORAL_HEALTH_TECH: {
        role: 'Behavioral Health Technician',
        ratio: '1:1000',
        duties: ['Assessment', 'Brief intervention', 'Referral coordination']
      },
      SOCIAL_WORKER: {
        role: 'Social Worker',
        ratio: '1:3000',
        duties: ['Counseling', 'Unit consultation', 'Family issues']
      }
    },
    DIVISION: {
      PSYCHIATRIST: {
        role: 'Psychiatrist',
        ratio: '1:5000',
        duties: ['Medication management', 'Complex cases', 'Command consultation']
      },
      PSYCHOLOGIST: {
        role: 'Psychologist',
        ratio: '1:3000',
        duties: ['Assessment', 'Therapy', 'Unit consultation']
      }
    }
  },
  
  BEDS: {
    RESTORATION: {
      type: 'Restoration Beds',
      ratio_per_1000: 2,
      location: 'Forward (Brigade)',
      hold_time_hours: 72,
      icon: ICONS.BED
    },
    STABILIZATION: {
      type: 'Stabilization Beds',
      ratio_per_1000: 1,
      location: 'Division/Corps',
      hold_time_days: 7,
      icon: ICONS.BED
    },
    PSYCHIATRIC: {
      type: 'Psychiatric Beds',
      ratio_per_1000: 0.5,
      location: 'Role 3/4',
      hold_time_days: 30,
      icon: ICONS.BED
    }
  }
};

// ============================================================================
// UKRAINE PSYCHOLOGICAL DATA 2022-2024
// ============================================================================

export const UKRAINE_PSYCH_DATA = {
  source: 'Ukraine 2022-2024 field observations',
  icon: ICONS.WARNING,
  
  csr_rates: {
    frontline_trenches: {
      location: 'Frontline Trenches',
      csr_rate_no_rotation: 0.40,
      csr_rate_with_rotation: 0.15,
      rotation_cycle_days: 4
    },
    artillery_exposed: {
      location: 'Under Artillery Fire',
      csr_rate_24h: 0.05,
      csr_rate_72h: 0.15,
      cumulative_30d: 0.45
    }
  },
  
  key_stressors: {
    artillery: {
      rank: 1,
      description: 'Constant artillery bombardment',
      psychological_effect: 'Helplessness, random death threat',
      icon: '💥'
    },
    drones: {
      rank: 2,
      description: 'Drone surveillance and FPV attacks',
      psychological_effect: 'Constant observation, inability to hide',
      icon: '🛸'
    },
    trench_conditions: {
      rank: 3,
      description: 'Muddy trenches, cold, no shelter',
      psychological_effect: 'Physical exhaustion, hopelessness',
      icon: '🌧️'
    },
    casualty_exposure: {
      rank: 4,
      description: 'Seeing dead/wounded comrades',
      psychological_effect: 'Survivor guilt, mortality awareness',
      icon: '💔'
    }
  },
  
  lessons_learned: [
    'Rotation schedules (3-4 days) dramatically reduce CSR',
    'Artillery is #1 psychological stressor',
    'Deep bunkers provide psychological protection',
    'Unit cohesion remains strongest protective factor',
    'Sleep deprivation rapidly degrades mental resilience'
  ],
  
  recommendations: [
    'Enforce 3-4 day rotation from frontline trenches',
    'Provide deep shelter for psychological recovery',
    'Maintain unit integrity during rotations',
    'Train leaders in stress recognition',
    'Pre-position mental health assets forward'
  ]
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get CSR intensity options for UI dropdown
 * @returns {Array} CSR intensity options
 */
export const getCSRIntensityOptions = () => {
  return Object.entries(CSR_RATES.BY_COMBAT_INTENSITY).map(([key, rate]) => ({
    value: key,
    label: rate.label,
    icon: rate.icon,
    color: rate.color,
    dailyRate: rate.daily_csr_rate,
    cumulative30d: rate.cumulative_30_day,
    sustainableDays: rate.sustainable_days
  }));
};

/**
 * Get rotation schedule options for UI dropdown
 * @returns {Array} Rotation options
 */
export const getRotationOptions = () => {
  return Object.entries(ROTATION_SCHEDULES).map(([key, schedule]) => ({
    value: key,
    label: schedule.label,
    icon: schedule.icon,
    frontlineDays: schedule.frontline_days,
    restDays: schedule.rest_days,
    cycleDays: schedule.cycle.total_cycle_days
  }));
};

/**
 * Get risk factor options for UI dropdown
 * @returns {Array} Risk factor options
 */
export const getRiskFactorOptions = () => {
  return Object.entries(CSR_RATES.RISK_FACTORS).map(([key, factor]) => ({
    value: key,
    label: factor.label,
    icon: factor.icon,
    multiplier: factor.multiplier || factor.multiplier_per_deployment,
    description: factor.description
  }));
};

/**
 * Get protective factor options for UI dropdown
 * @returns {Array} Protective factor options
 */
export const getProtectiveFactorOptions = () => {
  return Object.entries(CSR_RATES.PROTECTIVE_FACTORS).map(([key, factor]) => ({
    value: key,
    label: factor.label,
    icon: factor.icon,
    modifier: factor.modifier,
    description: factor.description
  }));
};

/**
 * Get PTSD deployment type options for UI dropdown
 * @returns {Array} Deployment type options
 */
export const getPTSDDeploymentOptions = () => {
  return Object.entries(PTSD_RATES.BY_DEPLOYMENT_TYPE).map(([key, rate]) => ({
    value: key,
    label: rate.label,
    icon: rate.icon,
    ptsdPercent: rate.ptsd_prevalence_percent
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get CSR rate for intensity level
 * @param {string} intensity - Intensity level
 * @returns {Object|null} CSR rate data
 */
export const getCSRRate = (intensity) => {
  return CSR_RATES.BY_COMBAT_INTENSITY[intensity] || null;
};

/**
 * Get rotation schedule for intensity
 * @param {string} intensity - Intensity level
 * @returns {Object|null} Rotation schedule
 */
export const getRotationSchedule = (intensity) => {
  return ROTATION_SCHEDULES[intensity] || ROTATION_SCHEDULES.MODERATE_INTENSITY;
};

/**
 * Calculate CSR casualties
 * @param {number} forceStrength - Force strength
 * @param {string} intensity - Combat intensity
 * @param {number} days - Duration in days
 * @param {Array} riskFactors - Active risk factors
 * @param {Array} protectiveFactors - Active protective factors
 * @returns {Object} CSR casualty estimate
 */
export const calculateCSRCasualties = (
  forceStrength,
  intensity = 'MODERATE_INTENSITY',
  days = 30,
  riskFactors = [],
  protectiveFactors = []
) => {
  const rate = getCSRRate(intensity);
  if (!rate) {
    return { error: 'Invalid intensity level', icon: ICONS.RED };
  }
  
  // Calculate risk multiplier
  let riskMultiplier = 1.0;
  const appliedRisks = [];
  
  riskFactors.forEach(rf => {
    const factor = CSR_RATES.RISK_FACTORS[rf];
    if (factor) {
      riskMultiplier *= factor.multiplier || 1.0;
      appliedRisks.push({ name: factor.label, multiplier: factor.multiplier });
    }
  });
  
  // Calculate protective modifier
  let protectiveModifier = 1.0;
  const appliedProtective = [];
  
  protectiveFactors.forEach(pf => {
    const factor = CSR_RATES.PROTECTIVE_FACTORS[pf];
    if (factor) {
      protectiveModifier *= factor.modifier;
      appliedProtective.push({ name: factor.label, modifier: factor.modifier });
    }
  });
  
  // Calculate base casualties
  const dailyRate = rate.daily_csr_rate * riskMultiplier * protectiveModifier;
  const totalCasualties = Math.round(forceStrength * dailyRate * days);
  
  // Calculate RTD
  const rtd24h = Math.round(totalCasualties * rate.rtd_rate_24h);
  const rtd72h = Math.round(totalCasualties * rate.rtd_rate_72h);
  const requireEvacuation = Math.round(totalCasualties * rate.evacuation_rate);
  
  // Determine status icon
  const casualtyRate = totalCasualties / forceStrength;
  let icon = ICONS.GREEN;
  if (casualtyRate > 0.20) icon = ICONS.RED;
  else if (casualtyRate > 0.10) icon = ICONS.ORANGE;
  else if (casualtyRate > 0.05) icon = ICONS.YELLOW;
  
  return {
    force_strength: forceStrength,
    intensity: rate.label,
    duration_days: days,
    
    daily_csr_rate: Math.round(dailyRate * 10000) / 10000,
    total_csr_casualties: totalCasualties,
    casualty_rate_percent: Math.round(casualtyRate * 100 * 10) / 10,
    
    rtd_within_24h: rtd24h,
    rtd_within_72h: rtd72h,
    require_evacuation: requireEvacuation,
    
    risk_multiplier: Math.round(riskMultiplier * 100) / 100,
    protective_modifier: Math.round(protectiveModifier * 100) / 100,
    applied_risks: appliedRisks,
    applied_protective: appliedProtective,
    
    sustainable_days: rate.sustainable_days,
    exceeds_sustainable: days > rate.sustainable_days,
    
    icon
  };
};

/**
 * Calculate rotation needs
 * @param {number} forceStrength - Force strength
 * @param {string} intensity - Combat intensity
 * @returns {Object} Rotation requirements
 */
export const calculateRotationNeeds = (forceStrength, intensity = 'MODERATE_INTENSITY') => {
  const schedule = getRotationSchedule(intensity);
  
  const frontlineStrength = Math.round(forceStrength * 0.33);
  const reserveStrength = Math.round(forceStrength * 0.33);
  const rearStrength = forceStrength - frontlineStrength - reserveStrength;
  
  const rotationCycleDays = schedule.cycle.total_cycle_days;
  const rotationsPerMonth = Math.round(30 / rotationCycleDays);
  
  return {
    intensity: schedule.label,
    schedule: schedule.cycle,
    
    frontline_strength: frontlineStrength,
    reserve_strength: reserveStrength,
    rear_strength: rearStrength,
    
    rotation_cycle_days: rotationCycleDays,
    rotations_per_month: rotationsPerMonth,
    max_frontline_days: schedule.max_continuous_days,
    
    transportation_requirement: `${Math.round(forceStrength / 3)} troops per rotation`,
    
    icon: schedule.icon
  };
};

/**
 * Calculate mental health support requirements
 * @param {number} forceStrength - Force strength
 * @param {string} intensity - Combat intensity
 * @returns {Object} Support requirements
 */
export const calculateMentalHealthSupport = (forceStrength, intensity = 'MODERATE_INTENSITY') => {
  const staffing = MENTAL_HEALTH_SUPPORT.STAFFING;
  const beds = MENTAL_HEALTH_SUPPORT.BEDS;
  
  const requirements = {
    staff: {},
    beds: {},
    total_mental_health_staff: 0
  };
  
  // Calculate staff needs
  const parseRatio = (ratioStr) => {
    const match = ratioStr.match(/1:(\d+)/);
    return match ? parseInt(match[1]) : 1000;
  };
  
  Object.entries(staffing.BATTALION).forEach(([key, data]) => {
    if (data.ratio) {
      const ratio = parseRatio(data.ratio);
      const needed = Math.ceil(forceStrength / ratio);
      requirements.staff[key] = { role: data.role, needed, duties: data.duties };
      requirements.total_mental_health_staff += needed;
    }
  });
  
  Object.entries(staffing.BRIGADE).forEach(([key, data]) => {
    if (data.ratio) {
      const ratio = parseRatio(data.ratio);
      const needed = Math.ceil(forceStrength / ratio);
      requirements.staff[key] = { role: data.role, needed, duties: data.duties };
      requirements.total_mental_health_staff += needed;
    }
  });
  
  // Intensity adjustment
  const intensityMultiplier = intensity.includes('HIGH') || intensity.includes('EXTREME') ? 1.5 : 1.0;
  
  // Bed calculations
  Object.entries(beds).forEach(([key, data]) => {
    const baseNeeded = Math.ceil((forceStrength / 1000) * data.ratio_per_1000);
    requirements.beds[key] = {
      type: data.type,
      needed: Math.ceil(baseNeeded * intensityMultiplier),
      location: data.location,
      icon: data.icon
    };
  });
  
  requirements.icon = ICONS.GREEN;
  
  return requirements;
};

/**
 * Estimate PTSD risk
 * @param {Object} riskFactors - Risk factors present
 * @param {string} deploymentType - Deployment type
 * @returns {Object} PTSD risk estimate
 */
export const estimatePTSDRisk = (riskFactors = {}, deploymentType = 'CONVENTIONAL_MODERATE') => {
  const deploymentData = PTSD_RATES.BY_DEPLOYMENT_TYPE[deploymentType] ||
                         PTSD_RATES.BY_DEPLOYMENT_TYPE.CONVENTIONAL_MODERATE;
  const baseRisk = deploymentData.ptsd_prevalence_percent / 100;
  
  let oddsMultiplier = 1.0;
  const factorsPresent = [];
  
  Object.entries(riskFactors).forEach(([factor, present]) => {
    if (present && PTSD_RATES.RISK_FACTORS[factor]) {
      oddsMultiplier *= PTSD_RATES.RISK_FACTORS[factor].odds_ratio;
      factorsPresent.push(PTSD_RATES.RISK_FACTORS[factor].label);
    }
  });
  
  const adjustedRisk = Math.min(0.80, baseRisk * oddsMultiplier);
  
  let riskLevel = 'LOW';
  let icon = ICONS.GREEN;
  if (adjustedRisk > 0.40) { riskLevel = 'HIGH'; icon = ICONS.RED; }
  else if (adjustedRisk > 0.20) { riskLevel = 'MODERATE'; icon = ICONS.ORANGE; }
  
  return {
    deployment_type: deploymentType,
    base_risk_percent: Math.round(baseRisk * 100),
    adjusted_risk_percent: Math.round(adjustedRisk * 100),
    risk_level: riskLevel,
    factors_present: factorsPresent,
    odds_multiplier: Math.round(oddsMultiplier * 10) / 10,
    icon
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate mental health configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateMentalHealthConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  if (config.high_intensity && !config.csc_team_deployed) {
    errors.push({
      code: 'NO_CSC',
      message: 'High-intensity operations require Combat Stress Control team',
      icon: ICONS.RED
    });
  }
  
  if (config.force_strength > 5000 && !config.psychiatrist_available) {
    warnings.push({
      code: 'NO_PSYCH',
      message: 'Forces >5000 should have psychiatrist access',
      icon: ICONS.YELLOW
    });
  }
  
  if (!config.peer_support_trained) {
    warnings.push({
      code: 'NO_PEER',
      message: 'Peer support program recommended for all deployments',
      icon: ICONS.YELLOW
    });
  }
  
  if (!config.rotation_plan && config.duration_days > 30) {
    errors.push({
      code: 'NO_ROTATION',
      message: 'Operations >30 days require rotation plan',
      icon: ICONS.RED
    });
  }
  
  return { 
    valid: errors.length === 0,
    errors, 
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if intensity level is valid
 * @param {string} intensity - Intensity level
 * @returns {boolean} True if valid
 */
export const isValidIntensity = (intensity) => {
  return intensity in CSR_RATES.BY_COMBAT_INTENSITY;
};

/**
 * Get all valid intensity levels
 * @returns {Array} Valid intensity levels
 */
export const getValidIntensityLevels = () => {
  return Object.keys(CSR_RATES.BY_COMBAT_INTENSITY);
};

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_MENTAL_HEALTH_CONFIG = {
  csc_team_deployed: true,
  psychiatrist_available: true,
  peer_support_trained: true,
  chaplain_support: true,
  restoration_beds: 10,
  screening_program: true,
  rotation_plan: true,
  icon: ICONS.GREEN
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const MENTAL_HEALTH_EDUCATIONAL = {
  key_principles: [
    'CSR is a NORMAL response to ABNORMAL stress - not weakness',
    'Early intervention at unit level maximizes RTD',
    'Unit cohesion is the single strongest protective factor',
    'Rotation schedules are not optional in high-intensity combat',
    'BICEPS: Brevity, Immediacy, Centrality, Expectancy, Proximity, Simplicity'
  ],
  
  ukraine_lessons: [
    'Artillery bombardment is primary psychological stressor',
    'Drone surveillance creates persistent hypervigilance',
    '30-40% CSR rates in frontline trenches without rotation',
    '3-4 day rotation cycles dramatically reduce CSR',
    'Deep bunkers provide psychological as well as physical protection'
  ],
  
  leader_responsibilities: [
    'Know your soldiers - notice changes in behavior',
    'Enforce sleep and rest - protect recovery time',
    'Maintain unit cohesion - keep teams together',
    'Model appropriate stress response - stay calm',
    'Remove stigma - treat CSR as injury, not weakness',
    'Buddy system - no one alone in combat'
  ],
  
  warning_signs: [
    'Withdrawal from peers',
    'Significant sleep problems',
    'Startle response / hypervigilance',
    'Inability to focus / make decisions',
    'Excessive anger or emotional numbing',
    'Reckless behavior or unnecessary risk-taking'
  ]
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named default export (fixes lint warning)
const mentalHealthConstants = {
  CSR_RATES,
  ROTATION_SCHEDULES,
  BICEPS_PRINCIPLES,
  PTSD_RATES,
  MENTAL_HEALTH_SUPPORT,
  UKRAINE_PSYCH_DATA,
  MENTAL_HEALTH_EDUCATIONAL,
  
  // UI Options
  getCSRIntensityOptions,
  getRotationOptions,
  getRiskFactorOptions,
  getProtectiveFactorOptions,
  getPTSDDeploymentOptions,
  
  // Helper functions
  getCSRRate,
  getRotationSchedule,
  calculateCSRCasualties,
  calculateRotationNeeds,
  calculateMentalHealthSupport,
  estimatePTSDRisk,
  
  // Validation
  validateMentalHealthConfig,
  isValidIntensity,
  getValidIntensityLevels,
  
  // Defaults
  DEFAULT_MENTAL_HEALTH_CONFIG
};

export default mentalHealthConstants;
